import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ApexPieChart } from './component/Charts'
import ColChart from './component/Charts/ColChart'
import ReportingTable from '../Table/component/Table/ReportingTable'
import { DateRangePicker } from '../Table/component/Table'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import axios from 'axios'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  columnDefinitionStationaryCombustion,
  columnDefinitionMobileCombustion,
  columnDefinitionFugitiveEmission,
  columnDefinitionProcessEmission
} from '../Table/component/Table/column'

const ScopeI = () => {
  const [dateRange, setDateRange] = useState({ start: '', end: '' })
  const [stationaryData, setStationaryData] = useState([])
  const [mobileData, setMobileData] = useState([])
  const [fugitiveData, setFugitiveData] = useState([])
  const [processData, setProcessData] = useState([])
  const [loadingStationary, setLoadingStationary] = useState(false)
  const [loadingMobile, setLoadingMobile] = useState(false)
  const [loadingFugitive, setLoadingFugitive] = useState(false)
  const [loadingProcess, setLoadingProcess] = useState(false)
  const [pieChartData, setPieChartData] = useState([0, 0, 0, 0]); // [stationary, mobile, fugitive, process]
  const [colChartData, setColChartData] = useState([[], [], [], []]); // 4 arrays of 12 months
  const year = new Date().getFullYear();

  const axiosPrivate = useAxiosPrivate()

  const navigate = useNavigate()
  const location = useLocation()

  // Columns for tables
  const columns = [
    { header: 'Category', accessorKey: 'category' },
    { header: 'Emission (Tonnes)', accessorKey: 'emission' },
    { header: 'Reported By', accessorKey: 'reportBy' },
  ]

  // Fetch data helper
  const fetchData = async (endpoint, setData, setLoading) => {
    setLoading(true)
    let isMounted = true
    const controller = new AbortController()

    try {
      const response = await axiosPrivate.get(endpoint, {
        signal: controller.signal,
      })
      if (isMounted) setData(response.data || [])
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error(`Failed to fetch ${endpoint}:`, err)
        if (err.response?.status === 403) {
          navigate('/sign-in', { state: { from: location }, replace: true })
        }
      }
    } finally {
      if (isMounted) setLoading(false)
    }

    return () => {
      isMounted = false
      controller.abort()
    }
  }

  const fetchScope1Totals = async (year) => {
    try {
      const response = await axiosPrivate.get(`/dashboards/s1-total/${year}`);
      setPieChartData(response.data.data || [0, 0, 0, 0]);
      console.log(pieChartData);
    } catch (err) {
      console.error('Failed to fetch Scope 1 totals:', err);
    }
  }

  const fetchScope1Monthly = async (year) => {
    try {
      const response = await axiosPrivate.get(`/dashboards/s1-monthly/${year}`);
      setColChartData(response.data.data || [Array(12).fill(0), Array(12).fill(0), Array(12).fill(0), Array(12).fill(0)]);
    } catch (err) {
      console.error('Failed to fetch Scope 1 monthly data:', err);
    }
  }

  useEffect(() => {
    fetchData('/stationaryCombustion/read-S1A-records', setStationaryData, setLoadingStationary)
    fetchData('/mobileCombustion/read-S1B-records', setMobileData, setLoadingMobile)
    fetchData('/fugitiveEmission/read-S1C-records', setFugitiveData, setLoadingFugitive)
    fetchData('/processEmission/read-S1D-records', setProcessData, setLoadingProcess)
    fetchScope1Totals(year);
    fetchScope1Monthly(year);
  }, [axiosPrivate, navigate, location])

  // Limit to 5 rows + pad empty rows
  const displayData = (data, keyPrefix) => [
    ...data.slice(0, 5),
    ...Array.from({ length: Math.max(0, 5 - data.length) }, (_, i) => ({
      id: `${keyPrefix}-empty-${i}`,
      category: '',
      emission: '',
      reportBy: '',
    })),
  ].slice(0, 5)

  const pieLabels = ['Stationary Combustion', 'Mobile Combustion', 'Fugitive Emission', 'Process Emission']
  const seriesNames = ['Stationary Combustion', 'Mobile Combustion', 'Fugitive Emission', 'Process Emission'];

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto max-h-full max-w-full">
      <div className="justify-between flex px-4 mt-4">
        <h2 className="text-xl font-semibold">Scope 1: Direct Emissions</h2>
        {/* <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} /> */}
      </div>

      {/* Charts */}
      <div className="grid pl-4 pr-4 rounded-3xl pt-4 text-maindarkbg dark:text-mainbg">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-6 sm:rounded-lg">
            <ApexPieChart data={pieChartData} labels={pieLabels} title="Total Emissions" height={400} />
          </div>
          <div className="col-span-12 lg:col-span-6 sm:rounded-lg">
            <ColChart data={colChartData} seriesNames={seriesNames} title="Category Breakdown" height={400} />
          </div>
        </div>
      </div>

      {/* Tables */}
      {/* Stationary Combustion Table */}
      <div className="px-4 mt-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Recent Stationary Combustion</h3>
          <Link to="/scope-i/stationary-combustion">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1 px-4 rounded">
              View All
            </button>
          </Link>
        </div>
        {loadingStationary ? <div>Loading...</div> :
          <ReportingTable
            data={displayData(stationaryData, 'stationary')}
            columns={columnDefinitionStationaryCombustion}
            selectedRows={new Set()}
          />
        }
      </div>

      {/* Mobile Combustion Table */}
      <div className="px-4 mt-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Recent Mobile Combustion</h3>
          <Link to="/scope-i/mobile-combustion">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1 px-4 rounded">
              View All
            </button>
          </Link>
        </div>
        {loadingMobile ? <div>Loading...</div> :
          <ReportingTable
            data={displayData(mobileData, 'mobile')}
            columns={columnDefinitionMobileCombustion}
            selectedRows={new Set()}
          />
        }
      </div>

      {/* Fugitive Emission Table */}
      <div className="px-4 mt-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Recent Fugitive Emissions</h3>
          <Link to="/scope-i/fugitive-emission">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1 px-4 rounded">
              View All
            </button>
          </Link>
        </div>
        {loadingFugitive ? <div>Loading...</div> :
          <ReportingTable
            data={displayData(fugitiveData, 'fugitive')}
            columns={columnDefinitionFugitiveEmission}
            selectedRows={new Set()}
          />
        }
      </div>

      {/* Process Emission Table */}
      <div className="px-4 mt-6 mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Recent Process Emissions</h3>
          <Link to="/scope-i/process-emission">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1 px-4 rounded">
              View All
            </button>
          </Link>
        </div>
        {loadingProcess ? <div>Loading...</div> :
          <ReportingTable
            data={displayData(processData, 'process')}
            columns={columnDefinitionProcessEmission}
            selectedRows={new Set()}
          />
        }
      </div>
    </div>
  )
}

export default ScopeI
